// Generated from the ad network's public pixel documentation (developer/ads/pixel and
// developer/ads/platforms/*), white-labeled and with the account id filled in at
// render time. Headings are `## `/`### ` paragraphs; PixelSetup renders them as h3/h4.
export type GuideBlock =
  | { type: "p"; text: string }
  | { type: "list"; items: string[] }
  | { type: "note"; text: string }
  | { type: "snippet"; label: string; extra: string }
  | { type: "code"; lang: string; label: string; code: string }
  | { type: "table"; head: string[]; rows: string[][] };
export type Guide = { key: string; name: string; group: string; intro: GuideBlock[]; steps: Array<{ title: string; blocks: GuideBlock[] }>; after: GuideBlock[] };

export const PIXEL_GUIDES: Guide[] = [
  {
    "key": "general",
    "name": "Any website",
    "group": "General",
    "intro": [
      {
        "type": "p",
        "text": "The pixel is a JavaScript snippet that you add to your website. It measures page views, links visitors to their purchases, and attributes conversion events back to your ads. Once installed, you'll see your website in the Websites page."
      }
    ],
    "steps": [
      {
        "title": "Install the snippet",
        "blocks": [
          {
            "type": "p",
            "text": "Paste this snippet inside the `<head>` of **every page** in your funnel: landing pages, advertorials, checkouts, and thank-you pages, not just your homepage."
          },
          {
            "type": "snippet",
            "label": "Pixel snippet",
            "extra": ""
          }
        ]
      },
      {
        "title": "Track events",
        "blocks": [
          {
            "type": "p",
            "text": "If your funnel has steps worth measuring, like a lead form, call booking, application, or a sale on your checkout, add `oxwell.track` calls to track those events."
          },
          {
            "type": "p",
            "text": "The most common setup is to fire events either:"
          },
          {
            "type": "list",
            "items": [
              "when a form submits successfully",
              "on the thank-you page or confirmation page the visitor sees after submitting"
            ]
          },
          {
            "type": "code",
            "lang": "javascript",
            "label": "Tracking events",
            "code": "    oxwell.track(\"lead\");                                      // a standard event\n    oxwell.track(\"schedule\", { value: 50, currency: \"USD\" });  // optionally with a value\n    oxwell.track(\"quiz_completed\");                            // or your own event name\n    "
          },
          {
            "type": "p",
            "text": "**It's important to include any information you have about the customer at every stage of the funnel.** If you accept a lead with a name, email and phone number, include those in the event fired to the platform."
          },
          {
            "type": "code",
            "lang": "javascript",
            "label": "",
            "code": "   oxwell.track(\"lead\", { name: \"John Doe\", email: \"johndoe@example.com\", phone: \"555-555-1234\" });\n   "
          }
        ]
      }
    ],
    "after": [
      {
        "type": "p",
        "text": "## Standard events"
      },
      {
        "type": "p",
        "text": "The pixel includes a set of standard event names for common stages of a funnel. Each one accepts an optional `value` (number) and `currency` (ISO 4217 code). The exception is `purchase`, which requires a positive `value`."
      },
      {
        "type": "table",
        "head": [
          "Event",
          "When to fire it"
        ],
        "rows": [
          [
            "`lead`",
            "A visitor submits contact info or an opt-in form"
          ],
          [
            "`schedule`",
            "A visitor books a call or appointment"
          ],
          [
            "`submit_application`",
            "A visitor submits an application"
          ],
          [
            "`contact`",
            "A visitor starts a conversation or contact request"
          ],
          [
            "`complete_registration`",
            "A signup or registration finishes"
          ],
          [
            "`view_content`",
            "A visitor views a key page or piece of content"
          ],
          [
            "`add_to_cart`",
            "A visitor adds an item to a cart"
          ],
          [
            "`purchase`",
            "A sale completes on your checkout. Requires a positive `value`"
          ]
        ]
      },
      {
        "type": "p",
        "text": "## Custom events"
      },
      {
        "type": "p",
        "text": "If the standard events don't cover your funnel's needs, or you want to track specific stages of the customer journey, you can fire custom events."
      },
      {
        "type": "code",
        "lang": "javascript",
        "label": "Custom event",
        "code": "oxwell.track(\"watched_vsl\");\noxwell.track(\"quiz_completed\", { value: 25, currency: \"USD\" });"
      },
      {
        "type": "p",
        "text": "Keep names short and stable, and reuse a small set. The platform stores names up to 250 characters."
      },
      {
        "type": "p",
        "text": "### Where to put the tracking call depends on what happens after the action"
      },
      {
        "type": "list",
        "items": [
          "**The action doesn't redirect** (e.g. a form that submits in place and shows an inline success message). Fire the event in the action's success handler, such as the form's `onSubmit` callback, after it succeeds:"
        ]
      },
      {
        "type": "code",
        "lang": "javascript",
        "label": "Track on submit (no redirect)",
        "code": "form.addEventListener(\"submit\", async (e) => {\n\te.preventDefault();\n\tawait submitForm();\n\toxwell.track(\"watched_vsl\");\n});"
      },
      {
        "type": "list",
        "items": [
          "**The action redirects** (e.g. a form that sends the visitor to a new page on submit). Fire the event on the page they land on, such as the thank-you or confirmation page. Make sure the pixel snippet is installed on that page too."
        ]
      },
      {
        "type": "p",
        "text": "## Track sales on your checkout"
      },
      {
        "type": "p",
        "text": "When a sale completes on your checkout, fire a `purchase` event. `value` is required: a purchase event with no positive value is rejected, because ad platforms can't optimize toward a sale with no amount. `currency` is optional and defaults to USD."
      },
      {
        "type": "code",
        "lang": "javascript",
        "label": "Purchase on your own checkout",
        "code": "oxwell.track(\"purchase\", {\n  value: 149.99,\n  currency: \"USD\",\n  event_id: \"order_8412\",\n  email: \"visitor@example.com\",\n});"
      },
      {
        "type": "p",
        "text": "These are forwarded to the ad platforms as their standard Purchase event, so a purchase-optimized campaign learns from them, and reporting attributes each sale to the ad that drove it. A sale that never reaches the pixel or the server-side API is invisible to attribution."
      },
      {
        "type": "p",
        "text": "## Give each event an ID"
      },
      {
        "type": "p",
        "text": "Pass an `event_id` with any event that could reach the platform more than once. For example, if you fire an event when landing on a thank you page, ensure that event is fired once per real conversion, not once per page view. The platform counts each `event_name` and `event_id` pair once, so a retry, a page refresh, or the same conversion sent from two places collapses into a single event."
      },
      {
        "type": "code",
        "lang": "javascript",
        "label": "Event with an ID",
        "code": "oxwell.track(\"lead\", { event_id: \"lead_8f21c0\" }); // one visitor's form submission\noxwell.track(\"lead\", { event_id: \"lead_2b94de\" }); // a different visitor's"
      },
      {
        "type": "p",
        "text": "**The ID identifies one single event, not the type of event.** Every lead needs its own. Send `\"lead\"` as the ID for every lead and the platform treats them all as one event, so hundreds of leads show up as one."
      },
      {
        "type": "p",
        "text": "Use a value your own system already has for that one action: a lead ID, order number, or form submission ID. Reuse that value only when you're sending that same action again. A retry counts as the same action. So does the same conversion mirrored from your server. A value made up at fire time is new every time, so it won't stop duplicates."
      },
      {
        "type": "p",
        "text": "If the same conversion can come from both your website and your server, send the same `event_id` from both so it lands as one event."
      },
      {
        "type": "p",
        "text": "## Attach customer information"
      },
      {
        "type": "p",
        "text": "Always send as much customer information as you have available. This helps the platform match events to customers and improve conversion tracking."
      },
      {
        "type": "p",
        "text": "These fields can be attached to any event type and are optional. Send only the fields you have."
      },
      {
        "type": "code",
        "lang": "javascript",
        "label": "Lead with customer fields",
        "code": "oxwell.track(\"lead\", {\n  email: \"visitor@example.com\",\n  first_name: \"Jane\",\n  last_name: \"Doe\",\n  name: \"Jane Doe\",\n  phone: \"+15551234567\",\n  external_id: \"customer_123\",\n  city: \"New York\",\n  state: \"NY\",\n  postal_code: \"10001\",\n  country: \"US\",\n});"
      },
      {
        "type": "table",
        "head": [
          "Field",
          "Description"
        ],
        "rows": [
          [
            "`email`",
            "Visitor's email address"
          ],
          [
            "`first_name`",
            "Visitor's first name"
          ],
          [
            "`last_name`",
            "Visitor's last name"
          ],
          [
            "`name`",
            "Visitor's full display name"
          ],
          [
            "`phone`",
            "Visitor's phone number"
          ],
          [
            "`external_id`",
            "Your own user/customer ID"
          ],
          [
            "`city`",
            "Visitor's city"
          ],
          [
            "`state`",
            "Visitor's state or region"
          ],
          [
            "`postal_code`",
            "Visitor's postal or ZIP code"
          ],
          [
            "`country`",
            "Visitor's country"
          ]
        ]
      },
      {
        "type": "note",
        "text": "Send these as plain text. A hashed email gets dropped because it isn't a valid address. A hashed phone number gets stored as meaningless digits and matches nobody."
      }
    ]
  },
  {
    "key": "clickfunnels",
    "name": "ClickFunnels",
    "group": "Hosting platforms",
    "intro": [],
    "steps": [
      {
        "title": "Open your funnel's custom code settings",
        "blocks": [
          {
            "type": "p",
            "text": "In ClickFunnels, open your funnel, go to **Funnel Settings > Custom Code**, then open the **Head Code** field."
          },
          {
            "type": "p",
            "text": "Setting this at the funnel level applies the pixel to every step in that funnel. To apply it across every funnel in your account instead, add it under your Site's global **Header Code** setting in the Sites app."
          }
        ]
      },
      {
        "title": "Paste the snippet",
        "blocks": [
          {
            "type": "p",
            "text": "Paste this into the **Head Code** field."
          },
          {
            "type": "snippet",
            "label": "Pixel snippet",
            "extra": ""
          },
          {
            "type": "p",
            "text": "Save the funnel. ClickFunnels adds this code to the `<head>` of every step automatically."
          }
        ]
      },
      {
        "title": "Confirm it's tracking",
        "blocks": [
          {
            "type": "p",
            "text": "Visit a live funnel step, then check the Websites list on this page. Your funnel's domain appears there once a page view comes through."
          }
        ]
      }
    ],
    "after": [
      {
        "type": "p",
        "text": "## Track leads and other funnel events"
      },
      {
        "type": "p",
        "text": "If your funnel has a form, application, or booking step, add a `oxwell.track` call for it: see Track events in the main pixel guide."
      },
      {
        "type": "note",
        "text": "**Sales on your checkout are not recorded on their own.** Fire `purchase` with the order value when a sale completes (on your order confirmation page, or from your checkout's success handler), or send it server-side. The full pixel reference covers both."
      }
    ]
  },
  {
    "key": "gohighlevel",
    "name": "GoHighLevel",
    "group": "Hosting platforms",
    "intro": [],
    "steps": [
      {
        "title": "Open your site or funnel's tracking code settings",
        "blocks": [
          {
            "type": "p",
            "text": "In GoHighLevel, go to **Sites > Funnels** (or Sites > Websites), open the funnel/website, then go to **Settings > Tracking Code**."
          },
          {
            "type": "p",
            "text": "Open the **Header Tracking** field. Setting this at the funnel level applies the pixel to every page inside it."
          }
        ]
      },
      {
        "title": "Paste the snippet",
        "blocks": [
          {
            "type": "p",
            "text": "Paste this into the **Head Tracking Code** field."
          },
          {
            "type": "snippet",
            "label": "Pixel snippet",
            "extra": ""
          },
          {
            "type": "p",
            "text": "Save the settings. GoHighLevel adds this code to the `<head>` of every page in the funnel or site."
          }
        ]
      },
      {
        "title": "Confirm it's tracking",
        "blocks": [
          {
            "type": "p",
            "text": "Visit a live page, then check the Websites list on this page. Your domain appears there once a page view comes through."
          }
        ]
      }
    ],
    "after": [
      {
        "type": "p",
        "text": "## Track leads and other funnel events"
      },
      {
        "type": "p",
        "text": "If your funnel has a form, application, or booking step, add a `oxwell.track` call for it: see Track events in the main pixel guide."
      },
      {
        "type": "note",
        "text": "**Sales on your checkout are not recorded on their own.** Fire `purchase` with the order value when a sale completes (on your order confirmation page, or from your checkout's success handler), or send it server-side. The full pixel reference covers both."
      }
    ]
  },
  {
    "key": "kajabi",
    "name": "Kajabi",
    "group": "Hosting platforms",
    "intro": [],
    "steps": [
      {
        "title": "Open your site tracking code settings",
        "blocks": [
          {
            "type": "p",
            "text": "In Kajabi, go to **Settings > Site Details > Page Scripts**, then open the **Header Page Scripts** field."
          },
          {
            "type": "p",
            "text": "This applies the pixel to every page on the site, including thank-you pages. It doesn't load on Kajabi's own checkout or account-creation pages, so a sale there is not recorded by itself: fire `purchase` with the order value on the thank-you page buyers land on after checkout, or send it server-side."
          }
        ]
      },
      {
        "title": "Paste the snippet",
        "blocks": [
          {
            "type": "p",
            "text": "Paste this into the **Header Page Scripts** field."
          },
          {
            "type": "snippet",
            "label": "Pixel snippet",
            "extra": ""
          },
          {
            "type": "p",
            "text": "Save the settings. Kajabi adds this code to the `<head>` of every page on the site."
          }
        ]
      },
      {
        "title": "Confirm it's tracking",
        "blocks": [
          {
            "type": "p",
            "text": "Visit a live page, then check the Websites list on this page. Your domain appears there once a page view comes through."
          }
        ]
      }
    ],
    "after": [
      {
        "type": "p",
        "text": "## Track leads and other funnel events"
      },
      {
        "type": "p",
        "text": "If your funnel has a form, application, or booking step, add a `oxwell.track` call for it: see Track events in the main pixel guide."
      },
      {
        "type": "note",
        "text": "**Sales on your checkout are not recorded on their own.** Fire `purchase` with the order value when a sale completes (on your order confirmation page, or from your checkout's success handler), or send it server-side. The full pixel reference covers both."
      }
    ]
  },
  {
    "key": "wordpress",
    "name": "WordPress",
    "group": "Hosting platforms",
    "intro": [],
    "steps": [
      {
        "title": "Install a header/footer script plugin",
        "blocks": [
          {
            "type": "p",
            "text": "WordPress doesn't expose the `<head>` tag by default. Install a plugin that lets you add custom code there, such as **Insert Headers and Footers** or **WPCode**, from **Plugins > Add New**."
          },
          {
            "type": "p",
            "text": "If your theme has a **Header Scripts** or **Custom Code** field in its own settings, you can use that instead of a plugin."
          }
        ]
      },
      {
        "title": "Paste the snippet",
        "blocks": [
          {
            "type": "p",
            "text": "Open the plugin's **Header** field. Paste this in."
          },
          {
            "type": "snippet",
            "label": "Pixel snippet",
            "extra": ""
          },
          {
            "type": "p",
            "text": "Save the changes. The plugin adds this code to the `<head>` of every page on the site, including pages built with Elementor, Divi, or other page builders."
          }
        ]
      },
      {
        "title": "Confirm it's tracking",
        "blocks": [
          {
            "type": "p",
            "text": "Visit a live page, then check the Websites list on this page. Your domain appears there once a page view comes through."
          }
        ]
      }
    ],
    "after": [
      {
        "type": "p",
        "text": "## Track leads and other funnel events"
      },
      {
        "type": "p",
        "text": "If your funnel has a form, application, or booking step, add a `oxwell.track` call for it: see Track events in the main pixel guide."
      },
      {
        "type": "note",
        "text": "**Sales on your checkout are not recorded on their own.** Fire `purchase` with the order value when a sale completes (on your order confirmation page, or from your checkout's success handler), or send it server-side. The full pixel reference covers both."
      }
    ]
  },
  {
    "key": "woocommerce",
    "name": "WooCommerce",
    "group": "Hosting platforms",
    "intro": [
      {
        "type": "p",
        "text": "WooCommerce is a WordPress plugin, so it uses the same `<head>` mechanism as any WordPress site: WordPress doesn't expose the `<head>` tag by default, so you need a plugin that adds custom code there."
      }
    ],
    "steps": [
      {
        "title": "Install a header/footer script plugin",
        "blocks": [
          {
            "type": "p",
            "text": "Install a plugin that lets you add custom code to the site header, such as **WPCode** or **Insert Headers and Footers**, from **Plugins > Add New**."
          }
        ]
      },
      {
        "title": "Paste the snippet",
        "blocks": [
          {
            "type": "p",
            "text": "Open the plugin's **Header** field. Paste this in."
          },
          {
            "type": "snippet",
            "label": "Pixel snippet",
            "extra": ""
          },
          {
            "type": "p",
            "text": "Save the changes. The plugin adds this code to the `<head>` of every page on the site, including the shop, product, and cart pages."
          }
        ]
      },
      {
        "title": "Confirm it's tracking",
        "blocks": [
          {
            "type": "p",
            "text": "Visit a live page, then check the Websites list on this page. Your domain appears there once a page view comes through."
          }
        ]
      }
    ],
    "after": [
      {
        "type": "p",
        "text": "## Track leads and other funnel events"
      },
      {
        "type": "p",
        "text": "If your store has a form, application, or booking step outside of checkout, add a `oxwell.track` call for it: see Track events in the main pixel guide."
      },
      {
        "type": "note",
        "text": "**Sales on your checkout are not recorded on their own.** Fire `purchase` with the order value when a sale completes (on your order confirmation page, or from your checkout's success handler), or send it server-side. The full pixel reference covers both."
      }
    ]
  },
  {
    "key": "shopify",
    "name": "Shopify",
    "group": "Hosting platforms",
    "intro": [
      {
        "type": "p",
        "text": "A Shopify install has two parts, and you need both:"
      },
      {
        "type": "list",
        "items": [
          "**The pixel snippet in your theme.** It runs on every storefront page: home, products, collections, cart: where it tracks visitors and lets the page checks on this screen see it.",
          "**A custom pixel.** Shopify's checkout runs in a sandbox that ignores theme scripts, so the snippet goes dark there. Shopify's own **Custom Pixel** feature is the way around that: it runs in every part of your store, checkout included, and reports standard events like `checkout_completed` and `payment_info_submitted`. The script in step 3 forwards each one to the platform, tied to the same visitor the theme snippet tracks."
        ]
      }
    ],
    "steps": [
      {
        "title": "Add the pixel snippet to your theme",
        "blocks": [
          {
            "type": "p",
            "text": "In Shopify, go to **Online Store > Themes**. On your current theme, select **⋯ > Edit code**. Under **Layout**, open `theme.liquid` and paste this snippet immediately before the closing `</head>` tag. then select **Save**."
          },
          {
            "type": "snippet",
            "label": "Pixel snippet",
            "extra": ""
          },
          {
            "type": "p",
            "text": "Because `theme.liquid` wraps every storefront page, this one edit covers your whole store. Don't paste the snippet into a single page or section template instead. A visitor who lands anywhere else wouldn't be tracked, and the install overlay would disappear as soon as you navigate off that page."
          }
        ]
      },
      {
        "title": "Add a custom pixel",
        "blocks": [
          {
            "type": "p",
            "text": "In Shopify, go to **Settings > Customer events**, then click **Add custom pixel**. Name it `Pixel`."
          }
        ]
      },
      {
        "title": "Paste the script",
        "blocks": [
          {
            "type": "p",
            "text": "Paste this into the pixel's code editor."
          },
          {
            "type": "p",
            "text": "The script reads the `_wuid` cookie the theme snippet sets, so checkout events land on the same visitor the theme snippet has been tracking. It also skips storefront page views: the theme snippet already reports those: and only sends page views from checkout, where theme scripts can't run."
          },
          {
            "type": "code",
            "lang": "javascript",
            "label": "Custom pixel",
            "code": "const PIXEL_ACCOUNT_ID = \"{{ACCOUNT_ID}}\";\n\nconst PIXEL_ENDPOINT = [\"https://t.wh\", \"op.tw/conversions\"].join(\"\");\n\nconst TRACKED_EVENTS = new Set([\n  \"product_viewed\",\n  \"collection_viewed\",\n  \"search_submitted\",\n  \"cart_viewed\",\n  \"product_added_to_cart\",\n  \"product_removed_from_cart\",\n  \"checkout_started\",\n  \"checkout_contact_info_submitted\",\n  \"checkout_address_info_submitted\",\n  \"checkout_shipping_info_submitted\",\n  \"payment_info_submitted\",\n  \"checkout_completed\",\n]);\n\nconst MAX_EVENT_NAME_LENGTH = 34;\n\nconst STANDARD_EVENTS = new Set([\n  \"page\",\n  \"lead\",\n  \"contact\",\n  \"schedule\",\n  \"submit_application\",\n  \"complete_registration\",\n  \"view_content\",\n  \"add_to_cart\",\n]);\n\n// Map onto the platform's standard event names wherever Shopify's event has a real\n// equivalent, a standard name skips the character-limit problem entirely,\n// since only custom names get prefixed with your company tag. Everything else\n// falls through to a shopify_-prefixed custom name below; Shopify's own names\n// for the three checkout steps are too long once prefixed, so they're\n// shortened here too.\nconst EVENT_NAME_OVERRIDES = {\n  page_viewed: \"page\",\n  product_viewed: \"view_content\",\n  product_added_to_cart: \"add_to_cart\",\n  checkout_completed: \"purchase\",\n  checkout_contact_info_submitted: \"shopify_checkout_contact\",\n  checkout_address_info_submitted: \"shopify_checkout_address\",\n  checkout_shipping_info_submitted: \"shopify_checkout_shipping\",\n};\n\nconst TRACKED_QUERY_PARAMS = [\n  \"utm_id\",\n  \"utm_source\",\n  \"utm_medium\",\n  \"utm_campaign\",\n  \"utm_term\",\n  \"utm_content\",\n  \"fbclid\",\n  \"gclid\",\n  \"ttclid\",\n  \"li_fat_id\",\n  \"msclkid\",\n  \"twclid\",\n  \"rdt_cid\",\n  \"sccid\",\n  \"wbraid\",\n  \"gbraid\",\n  \"ig_sid\",\n];\n\nfunction compact(object) {\n  return Object.fromEntries(\n    Object.entries(object).filter(\n      ([, value]) =>\n        value !== null &&\n        value !== undefined &&\n        value !== \"\",\n    ),\n  );\n}\n\nasync function readCookie(name) {\n  try {\n    return (await browser.cookie.get(name)) || null;\n  } catch {\n    return null;\n  }\n}\n\nasync function readCookies() {\n  const [fbp, fbc, ga, ttp] = await Promise.all([\n    readCookie(\"_fbp\"),\n    readCookie(\"_fbc\"),\n    readCookie(\"_ga\"),\n    readCookie(\"_ttp\"),\n  ]);\n\n  return { fbp, fbc, ga, ttp };\n}\n\n// On the very first page view of a visit the theme snippet may still be\n// loading, so give its _wuid cookie a moment to appear, otherwise that page\n// view would be reported twice.\nasync function readWuid(eventName) {\n  const wuid = await readCookie(\"_wuid\");\n  if (wuid || eventName !== \"page_viewed\") return wuid;\n\n  await new Promise((resolve) => setTimeout(resolve, 2000));\n  return readCookie(\"_wuid\");\n}\n\nfunction readQueryParameters(href) {\n  if (!href) return {};\n\n  try {\n    const url = new URL(href);\n\n    return compact(\n      Object.fromEntries(\n        TRACKED_QUERY_PARAMS.map((name) => [\n          name,\n          url.searchParams.get(name),\n        ]),\n      ),\n    );\n  } catch {\n    return {};\n  }\n}\n\nfunction readTimezone() {\n  try {\n    return Intl.DateTimeFormat()\n      .resolvedOptions()\n      .timeZone;\n  } catch {\n    return null;\n  }\n}\n\nfunction buildContext(event, cookies) {\n  const documentContext = event.context?.document;\n  const navigatorContext = event.context?.navigator;\n  const screen = event.context?.window?.screen;\n  const href = documentContext?.location?.href;\n\n  return compact({\n    user_agent: navigatorContext?.userAgent,\n    language: navigatorContext?.language,\n    timezone: readTimezone(),\n    screen_resolution: screen ? `${screen.width}x${screen.height}` : null,\n    fbp: cookies.fbp,\n    fbc: cookies.fbc,\n    ga: cookies.ga,\n    ttp: cookies.ttp,\n    ...readQueryParameters(href),\n  });\n}\n\n// Prefer the visitor ID the theme snippet stores in the _wuid cookie, so a\n// checkout is tied to the storefront visit that led to it. The shopify_\n// fallback covers pages where that cookie isn't readable.\nfunction getAnonymousId(clientId, wuid) {\n  if (wuid) return wuid;\n\n  if (!clientId) return null;\n\n  const anonymousId = `shopify_${clientId}`;\n\n  if (anonymousId.length > 64) {\n    console.error(\"Shopify client ID is too long\");\n    return null;\n  }\n\n  return anonymousId;\n}\n\nfunction getPixelEventName(shopifyEventName) {\n  const name =\n    EVENT_NAME_OVERRIDES[shopifyEventName] ||\n    `shopify_${shopifyEventName}`;\n\n  if (\n    !STANDARD_EVENTS.has(name) &&\n    name.length > MAX_EVENT_NAME_LENGTH\n  ) {\n    console.error(\n      \"Pixel event name too long, Meta will drop it\",\n      name,\n      name.length,\n    );\n  }\n\n  return name;\n}\n\nfunction getEventId(event, checkout) {\n  if (event.name === \"checkout_completed\") {\n    const orderOrCheckoutId =\n      checkout?.order?.id ||\n      checkout?.token ||\n      event.id;\n\n    return `shopify:purchase:${orderOrCheckoutId}`;\n  }\n\n  return `shopify:${event.name}:${event.id}`;\n}\n\nfunction buildUser(event, checkout, wuid) {\n  const anonymousId = getAnonymousId(event.clientId, wuid);\n\n  if (!anonymousId) return null;\n\n  const address =\n    checkout?.shippingAddress ||\n    checkout?.billingAddress ||\n    {};\n\n  const firstName = address.firstName || null;\n  const lastName = address.lastName || null;\n\n  return compact({\n    anonymous_id: anonymousId,\n    external_id:\n      checkout?.order?.customer?.id || null,\n    email: checkout?.email || null,\n    phone:\n      checkout?.phone ||\n      address.phone ||\n      null,\n    first_name: firstName,\n    last_name: lastName,\n    name:\n      [firstName, lastName]\n        .filter(Boolean)\n        .join(\" \") || null,\n    city: address.city || null,\n    state:\n      address.provinceCode ||\n      address.province ||\n      null,\n    postal_code: address.zip || null,\n    country:\n      address.countryCode ||\n      address.country ||\n      null,\n  });\n}\n\nfunction isCheckoutSurface(href) {\n  if (!href) return false;\n\n  try {\n    const path = new URL(href).pathname;\n    return path.includes(\"/checkouts\") || path.includes(\"/orders/\");\n  } catch {\n    return false;\n  }\n}\n\nasync function sendPixelEvent(payload) {\n  try {\n    const response = await fetch(PIXEL_ENDPOINT, {\n      method: \"POST\",\n      headers: {\n        \"Content-Type\": \"application/json\",\n      },\n      body: JSON.stringify(payload),\n      keepalive: true,\n      credentials: \"omit\",\n    });\n\n    const responseBody = await response.text();\n\n    if (!response.ok) {\n      console.error(\"Pixel event failed\", {\n        status: response.status,\n        response: responseBody,\n        payload,\n      });\n\n      return;\n    }\n\n    console.log(\n      \"Pixel event sent\",\n      payload.event_name,\n      responseBody,\n    );\n  } catch (error) {\n    console.error(\n      \"Pixel request failed\",\n      payload.event_name,\n      error,\n    );\n  }\n}\n\nasync function handleShopifyEvent(event) {\n  const href = event.context?.document?.location?.href || null;\n  const wuid = await readWuid(event.name);\n\n  // The theme snippet already reports storefront page views. Only checkout\n  // and the order status page, where theme scripts can't run, need their\n  // page views sent from here.\n  if (\n    event.name === \"page_viewed\" &&\n    wuid &&\n    !isCheckoutSurface(href)\n  ) {\n    return;\n  }\n\n  const checkout = event.data?.checkout;\n  const user = buildUser(event, checkout, wuid);\n\n  if (!user) {\n    console.warn(\n      \"Shopify event missing a visitor ID\",\n      event.name,\n    );\n    return;\n  }\n\n  const cookies = await readCookies();\n  const isPurchase =\n    event.name === \"checkout_completed\";\n\n  const payload = compact({\n    account_id: PIXEL_ACCOUNT_ID,\n    event_name: getPixelEventName(event.name),\n    event_id: getEventId(event, checkout),\n    event_time: event.timestamp,\n    action_source:\n      event.name === \"page_viewed\" ? null : \"website\",\n    url: href,\n    referrer_url:\n      event.context?.document?.referrer ||\n      null,\n    title:\n      event.context?.document?.title ||\n      null,\n    user,\n    context: buildContext(event, cookies),\n  });\n\n  if (\n    isPurchase &&\n    checkout?.totalPrice?.amount != null\n  ) {\n    payload.value = Number(\n      checkout.totalPrice.amount,\n    );\n\n    const currency =\n      checkout.totalPrice.currencyCode ||\n      checkout.currencyCode;\n\n    if (currency) {\n      payload.currency = currency.toLowerCase();\n    }\n  }\n\n  await sendPixelEvent(payload);\n}\n\nanalytics.subscribe(\"page_viewed\", handleShopifyEvent);\n\nanalytics.subscribe(\n  \"all_standard_events\",\n  async (event) => {\n    if (\n      event.name === \"page_viewed\" ||\n      !TRACKED_EVENTS.has(event.name)\n    ) {\n      return;\n    }\n\n    await handleShopifyEvent(event);\n  },\n);"
          }
        ]
      },
      {
        "title": "Save and connect the pixel",
        "blocks": [
          {
            "type": "p",
            "text": "Click **Save**, then **Connect**. Shopify won't send any events to an unconnected pixel."
          }
        ]
      },
      {
        "title": "Confirm it's tracking",
        "blocks": [
          {
            "type": "p",
            "text": "Visit a live storefront page, then check the Websites list on this page. Your domain appears there once a page view comes through. Then place a test order and confirm a purchase shows up too."
          },
          {
            "type": "p",
            "text": "When you verify the install live from the dashboard, the overlay follows you across storefront pages but disappears once you enter checkout: Shopify doesn't run theme scripts there. That's expected: the custom pixel keeps reporting checkout events, and the test purchase still shows up in your dashboard."
          }
        ]
      }
    ],
    "after": [
      {
        "type": "note",
        "text": "Keep the `checkout_completed` mapping. A Shopify order is processed entirely inside Shopify, and this script's `checkout_completed` (sent as `purchase`, with the order value) is the only thing that records it, so the ads that drove the sale get credit."
      }
    ]
  },
  {
    "key": "webinarjam",
    "name": "WebinarJam",
    "group": "Sales tools",
    "intro": [],
    "steps": [
      {
        "title": "Open your webinar's tracking settings",
        "blocks": [
          {
            "type": "p",
            "text": "In WebinarJam, open your webinar, go to **Integrations > Integrate a 3rd Party Tracking System**, then select **Embed Your Custom Tracking Code**."
          },
          {
            "type": "p",
            "text": "WebinarJam gives you a separate tracking field for each step of the funnel: the registration page, the registration form, the post-registration thank-you page, the live room, and the replay page."
          }
        ]
      },
      {
        "title": "Paste the tracking code into each field",
        "blocks": [
          {
            "type": "p",
            "text": " Paste each snippet into the matching field."
          },
          {
            "type": "p",
            "text": "**Registration Form Tracking** gets the plain pixel snippet. The registration form is a popup with its own context, separate from the page around it: the visitor's email, name, and phone fields live here, so this is what lets the pixel's automatic field capture actually see them:"
          },
          {
            "type": "snippet",
            "label": "Registration Form Tracking",
            "extra": ""
          },
          {
            "type": "p",
            "text": "**Registration Page Tracking** gets the pixel plus a listener that fires the `lead` event the instant registration completes: before the redirect to the thank-you page even happens:"
          },
          {
            "type": "snippet",
            "label": "Registration Page Tracking",
            "extra": "window.addEventListener(\"message\", function (e) {\n\tif (e.origin !== \"https://event.webinarjam.com\") return;\n\n\tlet data;\n\ttry {\n\t\tdata = typeof e.data === \"string\" ? JSON.parse(e.data) : e.data;\n\t} catch {\n\t\treturn;\n\t}\n\n\tif (data.func === \"redirect\" && typeof data.url === \"string\" && data.url.indexOf(\"/thank-you\") !== -1) {\n\t\toxwell.track(\"lead\");\n\t}\n});"
          },
          {
            "type": "p",
            "text": "**Post-Registration Thank You Page Tracking** gets the plain pixel snippet, as a backup page-view record:"
          },
          {
            "type": "snippet",
            "label": "Post-Registration Thank You Page Tracking",
            "extra": ""
          },
          {
            "type": "p",
            "text": "Add the plain pixel snippet to the live room and replay page fields too if you want visits to those steps tracked. Save your changes."
          },
          {
            "type": "note",
            "text": "Running registration on your own site instead of WebinarJam's hosted registration page? Install the snippet on that page directly: see the main pixel guide: since WebinarJam's tracking fields only cover its own hosted pages."
          }
        ]
      },
      {
        "title": "Confirm it's tracking",
        "blocks": [
          {
            "type": "p",
            "text": "Visit a live registration page, then check the Websites list on this page. Your domain appears there once a page view comes through. Then register with a test email and watch for a `lead` event to land the moment you complete the form."
          }
        ]
      }
    ],
    "after": [
      {
        "type": "note",
        "text": "**Sales on your checkout are not recorded on their own.** Fire `purchase` with the order value when a sale completes (on your order confirmation page, or from your checkout's success handler), or send it server-side. The full pixel reference covers both."
      }
    ]
  },
  {
    "key": "iclosed",
    "name": "iClosed",
    "group": "Sales tools",
    "intro": [
      {
        "type": "p",
        "text": "iClosed's booking calendar can't tell your website when someone books a call, so you can't track bookings the normal way. The fix: have iClosed send bookers to a \"Thank You\" page on your own site after they book. Put the pixel there instead, and it picks up the booker's name and email automatically."
      }
    ],
    "steps": [
      {
        "title": "Create a Thank You page",
        "blocks": [
          {
            "type": "p",
            "text": "Build a simple \"Thank You\" or \"Call Confirmed\" page wherever you already host your site. This is the page bookers land on right after they book a call."
          }
        ]
      },
      {
        "title": "Install the pixel on your booking page too",
        "blocks": [
          {
            "type": "p",
            "text": "This guide's other steps cover the Thank You page: but the pixel also needs to be on the page where people actually book the call (your landing page, or wherever the iClosed calendar is embedded). Without it there, you won't see page views or ad clicks for that page, only the final booking. Paste this into the `<head>` of that page."
          },
          {
            "type": "snippet",
            "label": "Pixel snippet",
            "extra": ""
          },
          {
            "type": "p",
            "text": "If your site builder doesn't let you edit the `<head>`, check its dedicated guide in this section: or, if you already use Google Tag Manager, add this same code there as a Custom HTML tag that fires on all pages."
          }
        ]
      },
      {
        "title": "Tell iClosed to send bookers to your page",
        "blocks": [
          {
            "type": "p",
            "text": "In your iClosed event settings, find **Confirmation Page** and switch it from the iClosed default to your own page's URL. Now every booking ends on your page instead of iClosed's."
          }
        ]
      },
      {
        "title": "Paste the pixel snippet onto your Thank You page",
        "blocks": [
          {
            "type": "p",
            "text": "Paste the same pixel snippet from step 2 into the `<head>` of your Thank You page too: every page in your funnel needs its own copy, not just the booking page."
          }
        ]
      },
      {
        "title": "Add one more snippet to record the booking",
        "blocks": [
          {
            "type": "p",
            "text": "Right under the pixel snippet on your Thank You page, paste this too. iClosed already includes the booker's name and email in the page's web address, and this picks them up automatically: you don't need to type anything in:"
          },
          {
            "type": "code",
            "lang": "html",
            "label": "Track a booking",
            "code": "<script>\nvar params = new URLSearchParams(window.location.search);\noxwell.track(\"schedule\", {\n\temail: params.get(\"invitee_email\") || undefined,\n\tname: params.get(\"invitee_full_name\") || undefined,\n});\n</script>"
          }
        ]
      },
      {
        "title": "Confirm it's tracking",
        "blocks": [
          {
            "type": "p",
            "text": "Book a call through the flow, then check the Websites list on this page. Your domain appears there once a page view comes through."
          }
        ]
      }
    ],
    "after": [
      {
        "type": "note",
        "text": "**Sales on your checkout are not recorded on their own.** Fire `purchase` with the order value when a sale completes (on your order confirmation page, or from your checkout's success handler), or send it server-side. The full pixel reference covers both."
      }
    ]
  },
  {
    "key": "calendly",
    "name": "Calendly",
    "group": "Sales tools",
    "intro": [
      {
        "type": "p",
        "text": "Calendly's booking page can't tell your website when someone books a call, so you can't track bookings the normal way. The fix: have Calendly send bookers to a \"Thank You\" page on your own site after they book. Put the pixel there instead, and it picks up the booker's name and email automatically."
      }
    ],
    "steps": [
      {
        "title": "Create a Thank You page",
        "blocks": [
          {
            "type": "p",
            "text": "Build a simple \"Thank You\" or \"Call Confirmed\" page wherever you already host your site. This is the page bookers land on right after they book a call."
          }
        ]
      },
      {
        "title": "Install the pixel on your booking page too",
        "blocks": [
          {
            "type": "p",
            "text": "This guide's other steps cover the Thank You page: but the pixel also needs to be on the page where people actually book the call (your landing page, or wherever the Calendly scheduler is embedded or linked from). Without it there, you won't see page views or ad clicks for that page, only the final booking. Paste this into the `<head>` of that page."
          },
          {
            "type": "snippet",
            "label": "Pixel snippet",
            "extra": ""
          },
          {
            "type": "p",
            "text": "If your site builder doesn't let you edit the `<head>`, check its dedicated guide in this section: or, if you already use Google Tag Manager, add this same code there as a Custom HTML tag that fires on all pages."
          }
        ]
      },
      {
        "title": "Tell Calendly to send bookers to your page",
        "blocks": [
          {
            "type": "p",
            "text": "Open your event type, then go to **Edit > More options > Confirmation page**. Under **After booking**, choose **Redirect to an external site**, and enter your Thank You page's URL. Turn on **Pass event details to your redirected page**: this is what makes Calendly send the booker's name and email along with the redirect."
          }
        ]
      },
      {
        "title": "Paste the pixel snippet onto your Thank You page",
        "blocks": [
          {
            "type": "p",
            "text": "Paste the same pixel snippet from step 2 into the `<head>` of your Thank You page too: every page in your funnel needs its own copy, not just the booking page."
          }
        ]
      },
      {
        "title": "Add one more snippet to record the booking",
        "blocks": [
          {
            "type": "p",
            "text": "Right under the pixel snippet on your Thank You page, paste this too. Calendly already includes the booker's name and email in the page's web address, and this picks them up automatically: you don't need to type anything in:"
          },
          {
            "type": "code",
            "lang": "html",
            "label": "Track a booking",
            "code": "<script>\nvar params = new URLSearchParams(window.location.search);\noxwell.track(\"schedule\", {\n\temail: params.get(\"invitee_email\") || undefined,\n\tname: params.get(\"invitee_full_name\") || undefined,\n});\n</script>"
          }
        ]
      },
      {
        "title": "Confirm it's tracking",
        "blocks": [
          {
            "type": "p",
            "text": "Book a call through the flow, then check the Websites list on this page. Your domain appears there once a page view comes through."
          }
        ]
      }
    ],
    "after": [
      {
        "type": "p",
        "text": "## Embedding Calendly on your own page instead?"
      },
      {
        "type": "p",
        "text": "If you embed Calendly's scheduler directly on a page (**Share > Add to Website**) rather than linking out to `calendly.com`, you can skip the redirect and listen for the booking to complete right there: the pixel snippet from step 2 still needs to be on that same page:"
      },
      {
        "type": "code",
        "lang": "javascript",
        "label": "Track a booking on an embedded calendar",
        "code": "window.addEventListener(\"message\", function (e) {\n\tconst isCalendlyEvent = e.origin === \"https://calendly.com\" && e.data.event && e.data.event.indexOf(\"calendly.\") === 0;\n\tif (!isCalendlyEvent || e.data.event !== \"calendly.event_scheduled\") return;\n\n\toxwell.track(\"schedule\");\n});"
      },
      {
        "type": "note",
        "text": "This message confirms a booking happened, but doesn't include the invitee's name or email: Calendly keeps those out of the client-side event for privacy. If you already collected the visitor's details earlier in your funnel, pass them into the same `track` call, or use the redirect method above instead."
      },
      {
        "type": "note",
        "text": "**Sales on your checkout are not recorded on their own.** Fire `purchase` with the order value when a sale completes (on your order confirmation page, or from your checkout's success handler), or send it server-side. The full pixel reference covers both."
      }
    ]
  }
];
